import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { PAGE_START, UI_TABLE_PAGE_SIZE } from '~/common/constants/page';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { User } from '~/components/Shared/User/User';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Tab } from '~/eds/src/types/Tab';
import { User as EdsUser } from '~/eds/src/types/User';
import { QueryParamType } from '~/enums/QueryParamType';
import { TableContextType } from '~/enums/TableContextType';
import { TicketStageType } from '~/enums/TicketStageType';
import { useAddToGroup } from '~/features/groups/hooks/useAddToGroup';
import { TurnTrackingPanel } from '~/features/turn-tracking/TurnTrackingPanel';
import { calculateDuration } from '~/features/turn-tracking/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withFlags } from '~/flags/withFlags';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useUsers } from '~/hooks/useUsers';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { intlService } from '~/i18n/IntlService';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';
import { TableTicket } from '~/types/Ticket';
import { ERROR } from '~/types/toast.types';
import { Uuid } from '~/types/Uuid';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';

import { getStatusLabel } from '../TicketsViewPage/TicketApprovalItem/utils';
import { useMergedFilters } from './hooks/useMergedFilters';
import { usePersistedFilters } from './hooks/usePersistedFilters';
import { getSortByFromColumns } from './PerformanceWorkflowTicketPage.util';
import WizardWorkflowTemplatesList from './TicketCreatePage/WizardWorkflowTemplatesList/WizardWorkflowTemplatesList';
import { TicketFilters } from './TicketFilters';
import { TokenStatusMessage } from './TokenStatusMessage/TokenStatusMessage';
import { useSearchTicketListQuery } from './useSearchTicketListQuery';
import {
  SortBy,
  TabsConfig,
  TicketCompletionType,
} from './workflowTicketTypes';

const { useState, useEffect } = React;

const SORT_DESC = 'desc';
const SORT_ASC = 'asc';

const stageOrder = ['edit', 'review', 'sign', 'finalize'];

const getTabsConfig = (): Record<TicketCompletionType, TabsConfig> => {
  const intl = intlService.getIntl();
  return {
    [TicketCompletionType.NeedsAction]: {
      value: TicketCompletionType.NeedsAction,
      label: intl.formatMessage({
        id: 'TicketList.needsActionTab',
        defaultMessage: 'Needs Action',
        description: 'Label for needs action tab in ticket list',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.needsActionEmptyTitle',
        defaultMessage: 'No tickets assigned to you.',
        description: 'Empty state title for needs action tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.needsActionEmptyMessage',
        defaultMessage:
          'Once you create a new ticket or when you are assigned to a ticket, it will be shown here.',
        description: 'Empty state message for needs action tab',
      }),
      filter: 'needsAction=true,completed=false',
    },
    [TicketCompletionType.InProgress]: {
      value: TicketCompletionType.InProgress,
      label: intl.formatMessage({
        id: 'TicketList.inProgressTab',
        defaultMessage: 'In Progress',
        description: 'Label for in progress tab in ticket list',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.inProgressEmptyTitle',
        defaultMessage: 'No active tickets.',
        description: 'Empty state title for in progress tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.inProgressEmptyMessage',
        defaultMessage: 'It looks like there are no tickets being reviewed.',
        description: 'Empty state message for in progress tab',
      }),
      filter: 'needsAction=false,completed=false',
    },
    [TicketCompletionType.Cancelled]: {
      value: TicketCompletionType.Cancelled,
      label: intl.formatMessage({
        id: 'TicketList.cancelledTab',
        defaultMessage: 'Canceled',
        description: 'Label for canceled tab in ticket list',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.cancelledEmptyTitle',
        defaultMessage: 'No canceled tickets.',
        description: 'Empty state title for canceled tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.cancelledEmptyMessage',
        defaultMessage: 'It looks like there are no canceled tickets.',
        description: 'Empty state message for canceled tab',
      }),
      filter: 'cancelled=true',
    },
    [TicketCompletionType.Done]: {
      value: TicketCompletionType.Done,
      label: intl.formatMessage({
        id: 'TicketList.doneTab',
        defaultMessage: 'Done',
        description: 'Label for done tab in ticket list',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.doneEmptyTitle',
        defaultMessage: 'No completed tickets.',
        description: 'Empty state title for done tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.doneEmptyMessage',
        defaultMessage: 'Once a ticket is finalized, it will be shown here.',
        description: 'Empty state message for done tab',
      }),
      filter: 'completed=true',
    },
    [TicketCompletionType.All]: {
      value: TicketCompletionType.All,
      label: intl.formatMessage({
        id: 'TicketList.allTab',
        defaultMessage: 'All',
        description: 'Label for all tab in ticket list',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.allEmptyTitle',
        defaultMessage: 'No tickets match your criteria.',
        description: 'Empty state title for all tab',
      }),
      filter: '',
    },
  };
};

function getActiveTabConfig(activeTab: number) {
  const tabsConfig = getTabsConfig();
  return Object.values(tabsConfig)[activeTab];
}

interface WorkflowTicketsPageProps {
  flags: Record<FlagType, boolean>;
}
/**
 * TODO: Try updating all 'any' types that are still being used.
 * TODO: Check if we can update activeTab parameter from a number to the tab name.
 * TODO: Update to new tooltip when placement is available.
 * TODO: Start removing ts-ignore as we start switching more of our code to typescript.
 * TODO: Look into typing ticket endpoints.
 */
const TicketList = ({ flags }: WorkflowTicketsPageProps) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const isESignatureV2Enabled = useFlag(FlagType.EsignatureV2Revamp);
  const hasGroupedTicketAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const {
    panel: addToGroupPanel,
    addToGroupBulkAction,
    addToGroupRowAction,
    canAddToGroup: canEditEntityGroups,
  } = useAddToGroup('ticket');
  const canAddToGroup = hasGroupedTicketAndDocs && canEditEntityGroups;
  const stageOrderLabels: Record<string, string> = {
    edit: intl.formatMessage({
      id: 'TicketList.editStage',
      defaultMessage: 'Edit',
      description: 'Label for the edit stage in ticket workflow',
    }),
    review: intl.formatMessage({
      id: 'TicketList.reviewStage',
      defaultMessage: 'Review',
      description: 'Label for the review stage in ticket workflow',
    }),
    sign: intl.formatMessage({
      id: 'TicketList.signStage',
      defaultMessage: 'Sign',
      description: 'Label for the sign stage in ticket workflow',
    }),
    finalize: intl.formatMessage({
      id: 'TicketList.finalizeStage',
      defaultMessage: 'Finalize',
      description: 'Label for the finalize stage in ticket workflow',
    }),
  };
  const { tableSettings } = useTableSettings(TableContextType.WorkflowTickets);
  const currentUser = useCurrentUser();
  const isWorkflowOnlyUser = checkIsWorkflowOnly(currentUser);
  const urlParams = {
    activeTab: getPageSearchQueryByKey(
      [QueryParamType.ActiveTab],
      TicketCompletionType.NeedsAction,
    ),
    page: getPageSearchQueryByKey([QueryParamType.Page], PAGE_START),
    pageSize:
      tableSettings?.pageSize ??
      getPageSearchQueryByKey([QueryParamType.PageSize], UI_TABLE_PAGE_SIZE),
    sortedColumn:
      tableSettings?.sortBy?.id ??
      getPageSearchQueryByKey([QueryParamType.SortedColumn], 'last activity'),
    sortedOrder:
      tableSettings?.sortBy?.desc !== undefined
        ? tableSettings.sortBy.desc
          ? SORT_DESC
          : SORT_ASC
        : getPageSearchQueryByKey([QueryParamType.SortedOrder], SORT_DESC),
  };
  const [activeTab, setActiveTab] = useState(urlParams.activeTab);
  const [pageIndex, setPageIndex] = useState(urlParams.page);
  const [initRender, setInitRender] = useState(true);
  const [sortBy, setSortBy] = useState<SortBy>({
    id: urlParams.sortedColumn,
    desc: urlParams.sortedOrder === SORT_DESC,
  });
  const [pageSize, setPageSize] = useState(
    tableSettings?.pageSize ?? urlParams.pageSize,
  );
  const [showTurnTrackingPanel, setShowTurnTrackingPanel] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const activeTabConfig = getActiveTabConfig(activeTab);

  const {
    filters: ticketQuery,
    updateFilters: setTicketQuery,
    clearFilters: clearTicketFilters,
    isLoaded: areFiltersLoaded,
  } = usePersistedFilters();
  // Read-only filters take precedence over conflicting persisted filters
  const mergedQuery = useMergedFilters(ticketQuery, activeTabConfig.value);
  const queryForApi = mergedQuery;

  // Generate merged queries for each tab's count (handles read-only filter precedence)
  const needsActionQuery = useMergedFilters(
    ticketQuery,
    TicketCompletionType.NeedsAction,
  );
  const inProgressQuery = useMergedFilters(
    ticketQuery,
    TicketCompletionType.InProgress,
  );
  const cancelledQuery = useMergedFilters(
    ticketQuery,
    TicketCompletionType.Cancelled,
  );

  const [throttledSearch, searchText, setSearchText] = useDebouncedState('');
  const [ticketId, setTicketId] = useState('');
  // TODO: change to empty string - phase 1 Turn Tracking
  // any call to getWorkflowParties currently works, update after decision on party implementation
  const [workflowId, setWorkflowId] = useState(uuid());

  const {
    data: templates,
    error: templatesError,
    isLoading: isTemplatesLoading,
  } = api.endpoints.getAvailableWorkflows.useQuery({});

  const usersData = useUsers();
  const dispatch = useDispatch();

  // TODO: can the creatorId be the user's extId. Since we already have the usersData have the extId make it easy to get the user.
  const [shouldShowCreateTicketList, setShouldShowCreateTicketList] =
    useState(false);

  const turnTrackingColumns = [
    {
      key: 'current party',
      sortField: 'current_party_type_Ticket',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'TicketList.currentPartyColumn',
        defaultMessage: 'Current Party',
        description: 'Column header for current party in turn tracking',
      }),
      width: 'm',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => {
        const statusChip = {
          text: ticket.currentParty,
          status: 'info',
        };
        return {
          chips: [statusChip],
        };
      },
    },
    {
      key: 'turns',
      sortField: 'turns_count_Ticket',
      align: 'center',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'TicketList.turnsColumn',
        defaultMessage: 'Turns',
        description: 'Column header for turns count in turn tracking',
      }),
      width: 'm',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => ({ number: ticket.turnsCount }),
    },
    {
      key: 'time with current party',
      sortField: 'current_turn_start_date_Ticket',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'TicketList.timeWithCurrentPartyColumn',
        defaultMessage: 'Time with Current Party',
        description:
          'Column header for time with current party in turn tracking',
      }),
      width: 'm',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => {
        const startDate = ticket?.currentTurnStartDate;
        if (!startDate) return {};
        const elapsedDays = calculateDuration(startDate, new Date());

        const elapsedDaysDisplay = formatNumber(elapsedDays, {
          unit: intl.formatMessage(
            {
              id: 'TicketList.daysCount',
              defaultMessage: '{count, plural, one {day} other {days}}',
              description: 'Unit for elapsed days',
            },
            {
              count: elapsedDays,
            },
          ),
          decimal: 0,
        });

        return {
          text: elapsedDaysDisplay,
        };
      },
    },
  ];

  const columns = [
    {
      key: 'name',
      sortField: 'name_Ticket',
      cellType: 'link',
      title: intl.formatMessage({
        id: 'TicketList.nameColumn',
        defaultMessage: 'Name',
        description: 'Column header for ticket name',
      }),
      mapCellProps: (ticket: TableTicket) => ({
        text: ticket.name,
        pathname: `/workflow/tickets/${ticket.id}`,
      }),
    },
    {
      key: 'status',
      sortField: 'stage_Ticket',
      cellType: 'steps',
      title: intl.formatMessage({
        id: 'TicketList.statusColumn',
        defaultMessage: 'Status',
        description: 'Column header for ticket status',
      }),
      width: 'm',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => {
        return {
          isCompleted: ticket.isCompleted,
          isCancelled: ticket.isCancelled,
          currentStepValue: ticket.currentStageName,
          defaultStepLabel: getStatusLabel(ticket.status, intl),
          steps: stageOrder
            .filter((stage: string) => {
              return ticket.enabledStages.includes(stage as TicketStageType);
            })
            .map((stage: string) => {
              return {
                label: stageOrderLabels[stage],
                value: stage,
              };
            }),
        };
      },
    },
    {
      key: 'submitted by',
      sortField: 'submitter_Ticket',
      cellType: 'user',
      title: intl.formatMessage({
        id: 'TicketList.submittedByColumn',
        defaultMessage: 'Submitted By',
        description: 'Column header for who submitted the ticket',
      }),
      minWidth: 'm',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => ({
        asyncUser: {
          id: ticket.creator,
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
    {
      key: 'submitted on',
      sortField: 'ticket_submit_date_Ticket',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'TicketList.submittedOnColumn',
        defaultMessage: 'Submitted On',
        description: 'Column header for when ticket was submitted',
      }),
      minWidth: 'm',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => {
        return {
          datetime: ticket.createdDate,
          format: 'full_datetime',
        };
      },
      width: 'm',
    },
    {
      key: 'last activity',
      sortField: 'ticket_last_activity_date_Ticket',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'TicketList.lastActivityColumn',
        defaultMessage: 'Last Activity',
        description: 'Column header for last activity date',
      }),
      width: 'm',
      disableResizing: true,
      mapCellProps: ({ lastActivityDate }: TableTicket) => ({
        datetime: lastActivityDate,
        format: 'relative',
      }),
    },
    {
      key: 'assigned to',
      sortField: 'assignee_Ticket',
      cellType: 'users',
      title: intl.formatMessage({
        id: 'TicketList.assignedToColumn',
        defaultMessage: 'Assigned To',
        description: 'Column header for who ticket is assigned to',
      }),
      width: 'm',
      align: 'left',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => {
        const users = usersData
          ? ticket.assignees.reduce((acc, id) => {
              if (usersData[id]) {
                acc.push(usersData[id]);
              }
              return acc;
            }, [] as EdsUser[])
          : [];

        return {
          users,
          limit: 4,
        };
      },
    },
    {
      key: 'participants',
      sortField: 'participant_Ticket',
      cellType: 'users',
      title: intl.formatMessage({
        id: 'TicketList.participantsColumn',
        defaultMessage: 'Participants',
        description: 'Column header for ticket participants',
      }),
      width: 'm',
      align: 'left',
      disableResizing: true,
      mapCellProps: (ticket: TableTicket) => {
        const users = usersData
          ? ticket.participants.reduce((acc, id) => {
              if (usersData[id]) {
                acc.push(usersData[id]);
              }
              return acc;
            }, [] as EdsUser[])
          : [];

        return {
          users,
          limit: 4,
        };
      },
    },
    ...turnTrackingColumns,
  ];

  const { count: needsActionCount, isLoading: needsActionLoading } =
    useSearchTicketListQuery({
      params: { pageSize: 0 },
      query: needsActionQuery,
    });
  const { count: cancelledCount, isLoading: cancelledLoading } =
    useSearchTicketListQuery({
      params: { pageSize: 0 },
      query: cancelledQuery,
    });
  const { count: inProgressCount, isLoading: inProgressLoading } =
    useSearchTicketListQuery({
      params: { pageSize: 0 },
      query: inProgressQuery,
    });

  const {
    data: ticketListData,
    count: ticketListCount,
    isLoading,
    error,
    isFetching,
  } = useSearchTicketListQuery({
    params: {
      page: pageIndex,
      pageSize: pageSize,
      sortBy: getSortByFromColumns(columns, sortBy),
    },
    searchText: throttledSearch,
    query: queryForApi,
  });

  const data = { data: ticketListData, count: ticketListCount };

  const shouldDisplayLoadingState =
    isLoading || inProgressLoading || needsActionLoading || cancelledLoading;

  useEffect(() => {
    updatePageSearchQuery(urlParams);
  }, []);

  useEffect(() => {
    if (data && isWorkflowOnlyUser && initRender) {
      setInitRender(false);
      const { count }: any = data;
      const workflowOnlyRoleDefaultTab = count
        ? TicketCompletionType.NeedsAction
        : TicketCompletionType.InProgress;

      if (workflowOnlyRoleDefaultTab !== activeTab && !count) {
        setActiveTab(workflowOnlyRoleDefaultTab);
        updatePageSearchQuery({ activeTab: workflowOnlyRoleDefaultTab });
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      if (coerceRtkqError(error)?.response?.data?.statusCode === 404) {
        return;
      }
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'TicketList.loadingError',
          defaultMessage: 'An error occurred while loading tickets.',
          description: 'Error message when tickets fail to load',
        }),
      );
    }
    if (templatesError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'TicketList.templatesError',
          defaultMessage: 'An error occurred while loading workflow templates.',
          description: 'Error message when workflow templates fail to load',
        }),
      );
    }
  }, [error, templatesError, intl]);

  const handleShowCreateTicketList = (showCreateTicketList: boolean) => {
    setShouldShowCreateTicketList(showCreateTicketList);
  };

  const handleTabClick = (nextActiveTab: string) => {
    setActiveTab(Number(nextActiveTab));
    setPageIndex(PAGE_START);
    updatePageSearchQuery(
      {
        [QueryParamType.ActiveTab]: Number(nextActiveTab),
        [QueryParamType.Page]: PAGE_START,
      },
      true,
    );
  };

  // Create internationalized tabs config
  const getInternationalizedTabsConfig = () => ({
    [TicketCompletionType.NeedsAction]: {
      value: TicketCompletionType.NeedsAction,
      label: intl.formatMessage({
        id: 'TicketList.needsActionLabel',
        defaultMessage: 'Needs Action',
        description: 'Tab label for tickets that need action',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.needsActionEmptyTitle',
        defaultMessage: 'No tickets assigned to you.',
        description: 'Empty state title for needs action tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.needsActionEmptyMessage',
        defaultMessage:
          'Once you create a new ticket or when you are assigned to a ticket, it will be shown here.',
        description: 'Empty state message for needs action tab',
      }),
      filter: 'needsAction=true,completed=false',
    },
    [TicketCompletionType.InProgress]: {
      value: TicketCompletionType.InProgress,
      label: intl.formatMessage({
        id: 'TicketList.inProgressLabel',
        defaultMessage: 'In Progress',
        description: 'Tab label for tickets in progress',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.inProgressEmptyTitle',
        defaultMessage: 'No active tickets.',
        description: 'Empty state title for in progress tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.inProgressEmptyMessage',
        defaultMessage: 'It looks like there are no tickets being reviewed.',
        description: 'Empty state message for in progress tab',
      }),
      filter: 'needsAction=false,completed=false',
    },
    [TicketCompletionType.Cancelled]: {
      value: TicketCompletionType.Cancelled,
      label: intl.formatMessage({
        id: 'TicketList.cancelledLabel',
        defaultMessage: 'Canceled',
        description: 'Tab label for canceled tickets',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.cancelledEmptyTitle',
        defaultMessage: 'No canceled tickets.',
        description: 'Empty state title for canceled tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.cancelledEmptyMessage',
        defaultMessage: 'It looks like there are no canceled tickets.',
        description: 'Empty state message for canceled tab',
      }),
      filter: 'cancelled=true',
    },
    [TicketCompletionType.Done]: {
      value: TicketCompletionType.Done,
      label: intl.formatMessage({
        id: 'TicketList.doneLabel',
        defaultMessage: 'Done',
        description: 'Tab label for completed tickets',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.doneEmptyTitle',
        defaultMessage: 'No completed tickets.',
        description: 'Empty state title for done tab',
      }),
      emptyMessage: intl.formatMessage({
        id: 'TicketList.doneEmptyMessage',
        defaultMessage: 'Once a ticket is finalized, it will be shown here.',
        description: 'Empty state message for done tab',
      }),
      filter: 'completed=true',
    },
    [TicketCompletionType.All]: {
      value: TicketCompletionType.All,
      label: intl.formatMessage({
        id: 'TicketList.allLabel',
        defaultMessage: 'All',
        description: 'Tab label for all tickets',
      }),
      emptyTitle: intl.formatMessage({
        id: 'TicketList.allEmptyTitle',
        defaultMessage: 'No tickets match your criteria.',
        description: 'Empty state title for all tab',
      }),
      filter: '',
    },
  });

  const renderEmptyTabContent = (
    tabName: TicketCompletionType,
  ): JSX.Element => {
    const tabConfig: TabsConfig = getInternationalizedTabsConfig()[tabName];

    return (
      <Layout
        direction="column"
        align="center"
        justify="center"
        spacing={2}
        py={16}
      >
        <Text variant="body-bold">{tabConfig?.emptyTitle}</Text>
        <Text variant="body">{tabConfig?.emptyMessage}</Text>
      </Layout>
    );
  };

  const renderErrorMessage = (): JSX.Element => (
    <Layout
      direction="column"
      align="center"
      justify="center"
      spacing={2}
      py={4}
    >
      <Text variant="body-bold">
        <FormattedMessage
          id="TicketList.errorTitle"
          defaultMessage="An error occurred retrieving tickets."
          description="Error title when tickets cannot be retrieved"
        />
      </Text>
      <Text variant="body">
        <FormattedMessage
          id="TicketList.errorMessage"
          defaultMessage="Refresh the page or reach out to out support team if the issue persists."
          description="Error message with instructions for user"
        />
      </Text>
    </Layout>
  );

  const render404Message = (): JSX.Element => (
    <Layout
      direction="column"
      align="center"
      justify="center"
      spacing={2}
      py={4}
    >
      <Text variant="body-bold">
        <FormattedMessage
          id="TicketList.noMatchTitle"
          defaultMessage="No tickets match your filter."
          description="Title when no tickets match the current filter"
        />
      </Text>
      <Text variant="body">
        <FormattedMessage
          id="TicketList.noMatchMessage"
          defaultMessage="You can create a new ticket or try a different filter."
          description="Message suggesting actions when no tickets match filter"
        />
      </Text>
    </Layout>
  );

  const renderSearchAndFilters = () => (
    <>
      {/* Search Input */}
      <Box mt={4} mb={4}>
        <SearchInput
          name="ticket-search"
          onChange={(value) => handleSearch(value || '')}
          placeholder={intl.formatMessage({
            id: 'Table.search.placeholder',
            defaultMessage: 'Search',
            description: 'Placeholder text for search input in table',
          })}
          value={searchText}
        />
      </Box>

      {/* Filters */}
      {areFiltersLoaded && (
        <Box mb={4}>
          <TicketFilters
            query={ticketQuery}
            onQueryChange={setTicketQuery}
            activeTab={activeTabConfig.value}
          />
        </Box>
      )}
    </>
  );

  const handleUpdate = (state: any, action: any) => {
    switch (action.type) {
      case 'toggleSortBy':
        if (state.sortBy.length) {
          const { id, desc } = state.sortBy[0];

          if (desc) {
            updatePageSearchQuery(
              { sortedOrder: SORT_DESC, sortedColumn: id },
              true,
            );
          } else {
            updatePageSearchQuery(
              { sortedOrder: SORT_ASC, sortedColumn: id },
              true,
            );
          }

          setSortBy(state.sortBy[0]);
        }
        break;
      case 'toggleRowSelected':
      case 'toggleAllRowsSelected':
      case 'toggleAllPageRowsSelected':
        setSelectedRowIds(Object.keys(state.selectedRowIds));
        break;
      default:
        return;
    }
  };

  const handlePagination = ({ pageIndex }: any) => {
    setPageIndex(pageIndex);
    updatePageSearchQuery({ page: pageIndex }, true);
  };

  const handleSearch = (searchText: string) => {
    setSearchText(searchText);
    setPageIndex(PAGE_START);
  };

  const handlePageSize = (value: number) => {
    updatePageSearchQuery({ pageSize: value }, true);
    setPageSize(value);
  };

  const handleOpenTurnTrackingPanel = async (ticketId: Uuid) => {
    setTicketId(ticketId);
    // TODO: setWorkflowId if necessary in line with parties discussion - phase 1 Turn Tracking
    setWorkflowId(uuid());
    setShowTurnTrackingPanel(true);
  };

  const getTable = (tableData: TableTicket[], count: number) => {
    const hasData = tableData.length > 0;
    const hasActiveFilters = ticketQuery.length > 0;
    const hasNoDataWithFilters =
      !hasData && hasActiveFilters && !isFetchingData && !initWorkFlowOnlyLoad;

    const placeholderContent = hasNoDataWithFilters
      ? {
          title: intl.formatMessage({
            id: 'TicketList.noMatchingResults',
            defaultMessage: 'No matching results',
            description: 'Title when no tickets match the applied filters',
          }),
          message: intl.formatMessage({
            id: 'TicketList.noDataWithFiltersMessage',
            defaultMessage:
              'Try adjusting your filters or resetting them to start over',
            description:
              'Message suggesting actions when filters return no results',
          }),
          action: {
            text: intl.formatMessage({
              id: 'Filters.resetFiltersButton',
              defaultMessage: 'Reset Filters',
              description: 'Button text to reset all filters',
            }),
            icon: 'reload' as const,
            variant: 'secondary' as const,
            onClick: clearTicketFilters,
          },
        }
      : undefined;

    return (
      <Layout direction="column" spacing={0}>
        {renderSearchAndFilters()}

        {/* Table */}
        <ContentContainer placeholderContent={placeholderContent}>
          <PersistedTable
            name="tickets"
            context={TableContextType.WorkflowTickets}
            columns={columns}
            data={tableData}
            options={{
              enableSearch: false, // Disable built-in search since we have custom one above
              enableExportXlsx: false,
              enableManageColumns: true,
              enableSelectRows: canAddToGroup,
              enablePagination: true,
              enablePageSizeSelect: true,
              enableEmptyState: true,
            }}
            state={{
              columnOrder: tableSettings?.columnOrder,
              globalFilter: searchText,
              pageSize,
              pageIndex,
              sortBy: [sortBy],
              selectedRowIds,
            }}
            onPaginate={handlePagination}
            onPageSizeChange={handlePageSize}
            onUpdate={handleUpdate}
            isLoading={isFetching}
            onSearch={handleSearch}
            getBulkActions={
              canAddToGroup
                ? ({ selectedRowIds }: { selectedRowIds: string[] }) => [
                    addToGroupBulkAction(selectedRowIds),
                  ]
                : undefined
            }
            reactTableOptions={{
              autoResetSortBy: false,
              autoResetSelectedRows: false,
              disableSortRemove: true,
              manualSortBy: true,
            }}
            totalCount={count}
            searchText={searchText}
            rowActions={[
              {
                label: intl.formatMessage({
                  id: 'TicketList.cycleTimeTracker',
                  defaultMessage: 'Cycle time tracker',
                  description: 'Row action label for cycle time tracker',
                }),
                onClick: (row: any) => handleOpenTurnTrackingPanel(row.id),
              },
              ...(canAddToGroup ? [addToGroupRowAction] : []),
            ]}
          />
        </ContentContainer>
      </Layout>
    );
  };

  const getCount = (tab: string) => {
    let count;
    switch (tab) {
      case 'Needs Action':
        count = needsActionCount;
        break;
      case 'In Progress':
        count = inProgressCount;
        break;
      case 'Cancelled':
        count = cancelledCount;
        break;
      default:
        count = undefined;
    }
    return count;
  };

  const getPanel = () => {
    const is404Error =
      coerceRtkqError(error)?.response?.data?.statusCode === 404;
    const hasData = ticketTableData.length > 0;
    const hasActiveFilters = ticketQuery.length > 0;
    const shouldRenderTable =
      hasData ||
      initWorkFlowOnlyLoad ||
      isFetchingData ||
      searchText.length > 0 ||
      hasActiveFilters;

    if (error) return is404Error ? render404Message() : renderErrorMessage();

    return shouldRenderTable
      ? getTable(ticketTableData, count)
      : renderEmptyTabContent(
          (activeTab as TicketCompletionType) ||
            TicketCompletionType.NeedsAction,
        );
  };

  // Deriving conditional page tabs
  const count = data.count;
  const tickets = data.data;
  const isFetchingData = isFetching;
  const ticketTableData: TableTicket[] = tickets;
  const initWorkFlowOnlyLoad = isWorkflowOnlyUser && initRender;
  const tabConfigs = Object.values(getTabsConfig());
  const internationalizedTabsConfig = getInternationalizedTabsConfig();
  const tabs: Tab[] = tabConfigs
    .filter((config) => !config.featureFlag || flags[config.featureFlag])
    .map((config) => ({
      label: internationalizedTabsConfig[config.value].label,
      value: String(config.value),
      count: getCount(config.label),
      panel: getPanel(),
    }));

  if (shouldShowCreateTicketList) {
    return (
      <WizardWorkflowTemplatesList
        templatesData={templates}
        hideModal={() => handleShowCreateTicketList(false)}
      />
    );
  }
  return (
    <>
      {isESignatureV2Enabled && <TokenStatusMessage />}
      <PageLayout
        actions={[
          {
            isLoading: isTemplatesLoading,
            text: intl.formatMessage({
              id: 'TicketList.newTicketButton',
              defaultMessage: 'New Ticket',
              description: 'Button text for creating a new ticket',
            }),
            level: 'primary',
            onClick: () => handleShowCreateTicketList(true),
            disabled: !templates || !templates.length,
          },
        ]}
        loadingContent={{
          isLoading: shouldDisplayLoadingState,
          message: intl.formatMessage({
            id: 'TicketList.loadingMessage',
            defaultMessage: 'Loading tickets…',
            description: 'Loading message while tickets are being fetched',
          }),
        }}
        tabs={{
          enableContentPadding: false,
          tabs,
          selectedTab: String(activeTab),
          onSelectTab: handleTabClick,
        }}
        title={intl.formatMessage({
          id: 'TicketList.pageTitle',
          defaultMessage: 'Tickets',
          description: 'Page title for the tickets list',
        })}
      />
      {showTurnTrackingPanel && ticketId && workflowId && (
        <TurnTrackingPanel
          mode="floating"
          ticketId={ticketId}
          workflowId={workflowId}
          isHidden={!showTurnTrackingPanel}
          onHide={() => {
            setShowTurnTrackingPanel(false);
            setTicketId('');
            setWorkflowId('');
            dispatch(slices.turnTracking.actions.reset());
          }}
        />
      )}
      {canAddToGroup && addToGroupPanel}
    </>
  );
};

export default withFlags(TicketList);
